import { DataType, DateTime, ExprForm } from '@mode-switch/express';
import { Names, Values } from '@mode-switch/model';
import { EncField, Encoding, ExprFrame, SortType, VariableType } from '@mode-vegas/vegas';
import { ChartTypes, OrgTypes } from '@mode/shared/contract-common';
import { getPlainTextFormat, getTextFormatOptionsByShortcutType } from '@mode/shared/util-js';
import { EncodingHelpers, makeSeriesNameFormatter } from '..';
import { DateTimePatterns } from './chart-encoding-helpers';
import { MISSING_VALUE, getFormatMapKeyByField } from './field-format-helpers';
import {
  DEFAULT_UNASSIGNED_COLOR,
  createFieldColors,
  getColorMapKeyByField,
  resolveFieldColor,
} from './helpers/chart-color';
import { MonthNamesByNumber, QuarterNamesByNumber, WeekdayNamesByNumber } from './helpers/chart-date';
import { SchemaModel } from './schema-model';

/**
 * Returns true if we should render this chart using the Muze renderer.
 */
export function useMuzeRenderer(chartType: ChartTypes.ChartType, format: Readonly<ChartTypes.ChartFormat> | undefined) {
  const vegasChartTypes = ['vegas', 'vegasPivotTable'];
  const nonChartTypes = ['bigValue', 'table', 'pivotTable'];
  const useMuzeRenderer = format?.useMuzeRenderer ?? false;
  return vegasChartTypes.includes(chartType) || (useMuzeRenderer && !nonChartTypes.includes(chartType));
}

/**
 * Checks the specified chartType for Muze rendering in Quick Charts. This is
 * only intended for HighCharts chart types. Other chart types will always
 * return false.
 */
export function isHighChartTypeEnabledForMuze(chartType: ChartTypes.ChartType) {
  // NOTE: Histogram is not included in this list even though it is not a HighCharts type
  // because it stores it's formats in 'formats' and expects them to get converted to 'fieldFormats'
  // when there is an encoding/format change.
  const nonHighChartsTypes = ['bigValue', 'table', 'pivotTable', 'vegas', 'vegasPivotTable'];
  return !nonHighChartsTypes.includes(chartType);
}

export enum HighChartsDisabledReason {
  UnsupportedChartType,
  ContinuousColor,
  UnsupportedSort,
  CustomFrame,
}

/**
 * Determines if a chart can be rendered using HighCharts based on the chart type and current
 * encoding.
 * @param chartType ChartTypes.ChartType
 * @param encoding Encoding
 * @param model SchemaModel. May be null though that will limit some checks.
 * @returns An array of HighChartsDisabledReason describing why the chart can not be rendered
 * with HighCharts. The array will be empty if the chart can be rendered.
 */
export function canRenderWithHighCharts(
  chartType: ChartTypes.ChartType,
  encoding: Encoding,
  model: SchemaModel | null
) {
  // Check that this is a HighChart type
  if (['bigValue', 'table', 'pivotTable', 'vegas', 'vegasPivotTable', 'histogram'].includes(chartType)) {
    return [HighChartsDisabledReason.UnsupportedChartType];
  }
  const reasons = [];
  // Check if the color field is continuous
  // HighCharts didn't support continuous color for any chart type
  const colorFields = (() => {
    if (encoding.marks.channel === '*') {
      return encoding.marks.properties.color.values;
    }
    return encoding.marks.series.reduce((colorFields, series) => {
      colorFields = colorFields.concat(series.properties.color.values);
      return colorFields;
    }, [] as EncField[]);
  })();
  if (colorFields.some((field) => field.variable.type === VariableType.CONTINUOUS)) {
    reasons.push(HighChartsDisabledReason.ContinuousColor);
  }
  // Check if there is a nested or manual sort applied
  if (
    encoding.sorts.some((sort) => {
      switch (sort.type) {
        case SortType.NESTED:
        case SortType.MANUAL:
          return true;
        default:
          return false;
      }
    })
  ) {
    reasons.push(HighChartsDisabledReason.UnsupportedSort);
  }

  // Check for a frame. HighCharts always left the frame empty
  const encFields = EncodingHelpers.collectEncFields(
    encoding,
    encoding.marks.channel === '*' ? '*' : encoding.marks.series[0].field
  ).filter((field) => {
    if (field.frame === undefined) {
      return false;
    }
    // Ignore the text field on a pie/donut because the user isn't
    // able to change this through the UI. Our default is a custom frame.
    if ((chartType === 'pie' || chartType === 'donut') && field === encoding.marks.properties.text.values[0]) {
      return false;
    }
    return true;
  });

  if (
    encFields.some((field) => {
      // No frame, then it is okay
      if (field.frame === undefined) {
        return false;
      }

      if (model !== null) {
        // Not analytic calc, then it is okay
        const compiled = model.compileEncField(field);
        if (!compiled.isAnalyticCalc) {
          return false;
        }

        // Quick Calcs aren't supported
        if (
          compiled.form === ExprForm.Difference ||
          compiled.form === ExprForm.PctDifference ||
          compiled.form === ExprForm.PctOfTotal
        ) {
          return true;
        }
      }

      // Quick charts default are pretty simple:
      // x encodings are Table-Down
      // otherwise Table-Across
      // First check that the type is Table. If not, it's custom
      if (field.frame.type !== ExprFrame.Type.Table) {
        return true;
      }
      // If it is x encoded, then make sure the addressing is down.
      if (encoding.marks.channel === 'x' && field.frame.address !== ExprFrame.Address.Down) {
        return true;
      } else if (encoding.marks.channel !== 'x' && field.frame.address !== ExprFrame.Address.Across) {
        return true;
      }
      return false;
    })
  ) {
    reasons.push(HighChartsDisabledReason.CustomFrame);
  }

  return reasons;
}

export function hideMeasureNamesHeader(encoding: Encoding) {
  let fieldFormats: ChartTypes.FieldFormatMap = {};

  const namesField = EncodingHelpers.findInEncoding(encoding, Names.Key);
  if (namesField != null) {
    const namesKey = getFormatMapKeyByField(namesField);
    const fieldFormat = makeDefaultFieldFormat();

    fieldFormat[ChartTypes.FieldFormatTypes.Header] = {
      [ChartTypes.FieldHeaderTypes.Title]: { text: '', show: false },
    };
    fieldFormats[namesKey] = fieldFormat;
  }

  return fieldFormats;
}

export function migrateFieldFormatsToVizExplorer(originalEncoding: Encoding, fieldFormats: ChartTypes.FieldFormatMap) {
  // If the original encoding contains Measure Values and there are values, copy the Measure Values setting to each
  // value.
  const mvField = EncodingHelpers.findInEncoding(originalEncoding, Values.Key);
  if (mvField == null || originalEncoding.value.length === 0) {
    // Nothing to convert.
    return fieldFormats;
  }
  const mvKey = getFormatMapKeyByField(mvField);
  const mvFieldFormat = fieldFormats[mvKey];
  const valueFieldFormatMap: ChartTypes.FieldFormatMap = {};
  if (mvFieldFormat != null) {
    originalEncoding.value.forEach((valueField) => {
      const key = getFormatMapKeyByField(valueField);
      valueFieldFormatMap[key] = { ...mvFieldFormat, baseFormat: { ...fieldFormats[key]?.baseFormat } };
    });
  }
  return {
    ...fieldFormats,
    ...valueFieldFormatMap,
  };
}

/**
 * Converts the old ChartFormat object into the new field-based FieldFormatMap for vegas charts
 * @param schemaModel
 * @param encoding
 * @param formats
 * @param oldChartType
 * @returns FieldFormatMap with formats for fields based on the specified ChartFormat.
 */
export function convertFormats(
  schemaModel: SchemaModel,
  encoding: Encoding,
  formats: Readonly<ChartTypes.ChartFormat>,
  oldChartType: ChartTypes.ChartType
) {
  let fieldFormats: ChartTypes.FieldFormatMap = {};
  convertLegendFormat(encoding, formats, fieldFormats);
  // For horizontal bar charts, the axes are swapped. The encoding has the correct field, but
  // the axes info in the ChartFormat is swapped compared to what you see in the formats panel.
  const isHorizontal = EncodingHelpers.HorizontalChartTypes.has(oldChartType);

  convertAxis(schemaModel, encoding.x[0], isHorizontal ? formats.yAxis : formats.xAxis, false, fieldFormats);
  convertAxis(schemaModel, encoding.y[0], isHorizontal ? formats.xAxis : formats.yAxis, isHorizontal, fieldFormats);
  convertAxis(schemaModel, encoding.y2[0], formats.y2Axis, false, fieldFormats);

  if (encoding.value.length > 0) {
    convertValues(encoding.value, formats, fieldFormats);
  }

  if (encoding.marks.properties.angle.values.length) {
    convertAngleLayer(encoding.marks.properties.angle.values[0], formats.xAxis, fieldFormats);
    if (encoding.marks.properties.text.values.length) {
      convertValues(encoding.marks.properties.text.values, formats, fieldFormats);
    }
  }

  // Set a percent format on any PctOfTotal fields
  EncodingHelpers.collectEncFields(encoding).forEach((field) =>
    setFormatForPctOfTotal(field, schemaModel, fieldFormats)
  );
  return fieldFormats;
}

/**
 * QuickCharts legend is always based on the color field. Map the legend settings to the
 * field map legend format.
 * @param encoding
 * @param formats
 * @param fieldFormats
 */
function convertLegendFormat(
  encoding: Encoding,
  formats: Readonly<ChartTypes.ChartFormat>,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  const colorField =
    encoding.marks.channel === '*'
      ? encoding.marks.properties.color.values[0]
      : encoding.marks.series[0].properties.color.values[0];
  if (colorField != null && formats.legend != null) {
    const isMeasureNames = colorField.formula === Names.Key;
    const colorKey = getColorMapKeyByField(colorField);
    const fieldFormat = fieldFormats[colorKey] ?? makeDefaultFieldFormat();
    const hasTitle =
      (formats.legend.title != null && formats.legend.title.length > 0) ||
      formats.legend?.baseFormat?.displayName != null;
    fieldFormat[ChartTypes.FieldFormatTypes.Legend] = {
      [ChartTypes.FieldLegendTypes.Title]: formats.legend.title as string,
      [ChartTypes.FieldLegendTypes.Position]: formats.legend.position,
      [ChartTypes.FieldLegendTypes.Show]: formats.legend.enabled,
      [ChartTypes.FieldLegendTypes.ShowTitle]: formats.legend.titleEnabled && !(isMeasureNames && !hasTitle),
    };
    fieldFormat[ChartTypes.FieldFormatTypes.Base] = formats.legend.baseFormat;
    fieldFormats[colorKey] = fieldFormat;
  }
}

/**
 * Converts the axis settings to formats based on the field. The formats are inserted into the
 * passed fieldFormats
 * @param schemaModel
 * @param encField
 * @param axisInfo
 * @param fieldFormats
 */
function convertAxis(
  schemaModel: SchemaModel,
  encField: EncField,
  axisInfo: Readonly<{
    dateFormat?: ChartTypes.DateFormat;
    numberRange: Readonly<ChartTypes.ChartNumberRange>;
    numberFormat: Readonly<ChartTypes.ChartNumberFormat>;
    titleEnabled: boolean;
    title?: string | null;
    rotate?: ChartTypes.LabelRotation | null;
    scaleType?: string | null;
    axisLabels?: string | null;
    showAxisLabels?: boolean | null;
    baseFormat?: ChartTypes.BaseFieldFormat | undefined;
    valueFieldFormatMap?: ChartTypes.ValueFieldFormatMap | undefined;
  }>,
  isSecondaryY: boolean,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  if (encField != null && axisInfo != null) {
    const key = getFormatMapKeyByField(encField);
    const fieldFormat = fieldFormats[key] ?? makeDefaultFieldFormat();
    if (encField.variable.type === VariableType.CONTINUOUS) {
      // For continuous, configure both the Axis and the Pane since the
      // value might also be used for data label formatting.
      fieldFormat[ChartTypes.FieldFormatTypes.Axis] = {
        [ChartTypes.FieldAxisTypes.Title]: {
          show: axisInfo.titleEnabled,
          text: axisInfo.title as string,
        },
        [ChartTypes.FieldAxisTypes.Scale]: convertScaleFormat(schemaModel, encField, axisInfo),
        [ChartTypes.FieldAxisTypes.Label]: {
          // Muze and Highcharts have opposite interpretations of rotation angle
          rotation: axisInfo.rotate != null ? -axisInfo.rotate : undefined,
          showLabels: axisInfo.showAxisLabels != null ? axisInfo.showAxisLabels : undefined,
        },
        [ChartTypes.FieldAxisTypes.BinnedAxisLabel]:
          axisInfo.axisLabels === 'continuousRange'
            ? { type: ChartTypes.BinnedAxisLabelTypes.Continuous }
            : axisInfo.axisLabels === 'firstValueOnly'
              ? { type: ChartTypes.BinnedAxisLabelTypes.First }
              : { type: ChartTypes.BinnedAxisLabelTypes.Auto },
      };
      fieldFormat[ChartTypes.FieldFormatTypes.Pane] = {
        [ChartTypes.FieldPaneTypes.Default]: buildTextFormat(schemaModel, encField, axisInfo),
      };
    } else {
      // For discrete, the Header is used rather than the Axis.
      fieldFormat[ChartTypes.FieldFormatTypes.Header] = {
        [ChartTypes.FieldPaneTypes.Default]: buildTextFormat(schemaModel, encField, axisInfo),
        [ChartTypes.FieldHeaderTypes.Title]: {
          show: axisInfo.titleEnabled,
          text: axisInfo.title as string,
          location: isSecondaryY ? ChartTypes.HeaderAxisLocationTypes.Axis : ChartTypes.HeaderAxisLocationTypes.Auto,
        },
        [ChartTypes.FieldHeaderTypes.Label]: {
          // Muze and Highcharts have opposite interpretations of rotation angle
          rotation: axisInfo.rotate != null ? -axisInfo.rotate : undefined,
        },
      };
    }
    fieldFormat[ChartTypes.FieldFormatTypes.Base] =
      axisInfo?.baseFormat || axisInfo?.valueFieldFormatMap?.[encField.formula]?.baseFormat;

    fieldFormats[key] = fieldFormat;
  }
}

/**
 * Converts the axis format info into a ScaleFormat
 * @param schemaModel
 * @param field
 * @param formatInfo
 * @returns ScaleFormat
 */
function convertScaleFormat(
  schemaModel: SchemaModel,
  field: EncField,
  formatInfo: {
    dateFormat?: ChartTypes.DateFormat;
    numberRange: Readonly<ChartTypes.ChartNumberRange>;
    numberFormat: Readonly<ChartTypes.ChartNumberFormat>;
    scaleType?: string | null;
  }
) {
  const scaleTypeFormat =
    formatInfo.scaleType === 'logarithmic'
      ? { type: ChartTypes.AxisScaleTypes.Log, base: 10 }
      : { type: ChartTypes.AxisScaleTypes.Linear };

  const scaleFormat: ChartTypes.ScaleFormat = {
    [ChartTypes.FieldAxisTypes.Default]: getPlainTextFormat(field.formula, DataType.Name.String),
    [ChartTypes.FieldAxisTypes.Range]: {
      isIndependent: false,
    },
    [ChartTypes.FieldAxisTypes.Interpolation]: scaleTypeFormat,
  };

  const compiled = schemaModel.compileEncField(field);
  if (compiled.isValid) {
    if (compiled.isFormulaDate) {
      scaleFormat[ChartTypes.FieldAxisTypes.Default] = buildTextFormat(schemaModel, field, formatInfo);
    } else if (compiled.isFormulaNumeric) {
      scaleFormat[ChartTypes.FieldAxisTypes.Default] = buildTextFormat(schemaModel, field, formatInfo);
      // Add range
      const min = formatInfo.numberRange?.min != null ? +formatInfo.numberRange.min : undefined;
      const max = formatInfo.numberRange?.max != null ? +formatInfo.numberRange.max : undefined;
      scaleFormat[ChartTypes.FieldAxisTypes.Range] = {
        isIndependent: false,
        min,
        max,
      };
    }
  }
  return scaleFormat;
}

/**
 * Converts the axis format info into a TextFormat
 * @param schemaModel
 * @param field
 * @param formatInfo
 * @returns TextFormat
 */
function buildTextFormat(
  schemaModel: SchemaModel,
  field: EncField,
  formatInfo: {
    dateFormat?: ChartTypes.DateFormat;
    numberRange: Readonly<ChartTypes.ChartNumberRange>;
    numberFormat: Readonly<ChartTypes.ChartNumberFormat>;
  }
) {
  let textFormat = getPlainTextFormat(field.formula, DataType.Name.String);
  const compiled = schemaModel.compileEncField(field);
  if (compiled.isValid) {
    if (compiled.isFormulaDate) {
      textFormat = {
        type: ChartTypes.FormatType.Date,
        colType: compiled.formulaDataType,
        shortcutType: ChartTypes.FormatShortcutType.Automatic,
        dateFormat: formatInfo.dateFormat,
      };
      // Convert the date formats
      switch (formatInfo.dateFormat) {
        case 'hour':
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.DateAndTime;
          break;
        case 'full_mon':
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.MonthYear;
          break;
        case 'mon_day':
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.MonthDay;
          break;
        case 'year':
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.Year;
          break;
        case 'auto':
        case 'none':
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.Automatic;
          break;
        default:
          textFormat['shortcutType'] = ChartTypes.FormatShortcutType.Custom;
          break;
      }
    } else if (compiled.isFormulaNumeric) {
      const formatType = formatInfo.numberFormat.format;
      switch (formatType) {
        case 'percent':
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.Percent),
            type: ChartTypes.FormatType.Percent,
            shortcutType: ChartTypes.FormatShortcutType.Percent,
          };
          break;
        case 'currency':
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.Currency),
            type: ChartTypes.FormatType.Financial,
            shortcutType: ChartTypes.FormatShortcutType.Currency,
          };
          break;
        case 'thousands':
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.ShortNumber),
            type: ChartTypes.FormatType.Number,
            shortcutType: ChartTypes.FormatShortcutType.Custom,
            displayUnit: 'K',
          };
          break;
        case 'millions':
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.ShortNumber),
            type: ChartTypes.FormatType.Number,
            shortcutType: ChartTypes.FormatShortcutType.Custom,
            displayUnit: 'M',
          };
          break;
        case 'billions':
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.ShortNumber),
            type: ChartTypes.FormatType.Number,
            shortcutType: ChartTypes.FormatShortcutType.Custom,
            displayUnit: 'B',
          };
          break;
        default:
          textFormat = {
            ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.ShortNumber),
            type: ChartTypes.FormatType.Number,
            shortcutType: ChartTypes.FormatShortcutType.Automatic,
          };
          break;
      }
      textFormat.colType = compiled.formulaDataType;
      textFormat.precision = formatInfo.numberFormat.precision == null ? undefined : formatInfo.numberFormat.precision;
    }
  }
  return textFormat;
}

function convertValues(
  encFields: Readonly<EncField[]>,
  formats: ChartTypes.ChartFormat | undefined,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  if (encFields != null && formats != null) {
    encFields.forEach((field) => {
      const key = getFormatMapKeyByField(field);
      let fieldFormat = fieldFormats[key] ?? makeDefaultFieldFormat();
      fieldFormat[ChartTypes.FieldFormatTypes.Base] =
        formats.yAxis?.valueFieldFormatMap?.[field.formula]?.[ChartTypes.FieldFormatTypes.Base];
      fieldFormats[key] = fieldFormat;
    });
  }
}

function convertAngleLayer(
  encField: EncField,
  axisInfo: Readonly<{
    dateFormat?: ChartTypes.DateFormat;
    numberRange: Readonly<ChartTypes.ChartNumberRange>;
    numberFormat: Readonly<ChartTypes.ChartNumberFormat>;
    titleEnabled: boolean;
    title?: string | null;
    rotate?: ChartTypes.LabelRotation | null;
    scaleType?: string | null;
    axisLabels?: string | null;
    showAxisLabels?: boolean | null;
    baseFormat?: ChartTypes.BaseFieldFormat | undefined;
  }>,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  if (encField != null && axisInfo != null) {
    const key = getFormatMapKeyByField(encField);
    const fieldFormat = fieldFormats[key] ?? makeDefaultFieldFormat();
    fieldFormat[ChartTypes.FieldFormatTypes.Base] = axisInfo?.baseFormat;
    fieldFormats[key] = fieldFormat;
  }
}

function setFormatForPctOfTotal(field: EncField, schemaModel: SchemaModel, fieldFormats: ChartTypes.FieldFormatMap) {
  if (field) {
    const compiledTextField = schemaModel.compileEncField(field);
    if (compiledTextField.isValid && compiledTextField.form === ExprForm.PctOfTotal) {
      const key = getFormatMapKeyByField(field);
      const fieldFormat = fieldFormats[key] ?? makeDefaultFieldFormat();
      fieldFormat[ChartTypes.FieldFormatTypes.Pane] = {
        [ChartTypes.FieldPaneTypes.Default]: {
          type: ChartTypes.FormatType.Percent,
          shortcutType: ChartTypes.FormatShortcutType.Percent,
          ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.Percent),
        },
      };
      fieldFormats[key] = fieldFormat;
    }
  }
}

export function makeDefaultFieldFormat() {
  return {
    [ChartTypes.FieldFormatTypes.Axis]: {},
    [ChartTypes.FieldFormatTypes.Header]: {},
    [ChartTypes.FieldFormatTypes.Legend]: {},
    [ChartTypes.FieldFormatTypes.Pane]: {},
  };
}

/**
 * Converts the old ChartFormat object into the new field-based FieldColorMap for vegas charts.
 *
 * @param {SchemaModel} model model
 * @param {Encoding} encoding encoding
 * @param {Readonly<ChartTypes.ChartFormat>} format old ChartFormat object
 * @param {ChartTypes.ChartSnapshot} snapshot chart snapshot
 * @param {Readonly<OrgTypes.ColorPalette[]>} palettes color palettes
 * @returns {ChartTypes.FieldColorMap} new field-based FieldColorMap for vegas charts
 */
export function convertColors(
  model: SchemaModel,
  encoding: Encoding,
  format: Readonly<ChartTypes.ChartFormat>,
  snapshot: ChartTypes.ChartSnapshot,
  palettes: Readonly<OrgTypes.ColorPalette[]>,
  currentColorDomain: ReadonlyArray<string | null | undefined>
): ChartTypes.FieldColorMap {
  let fieldColors: ChartTypes.FieldColorMap = snapshot.fieldColors;

  // QuickCharts and Vegas charts use the same color palette but different mappings,
  // so look up the colors to create a new indexed map.
  const field =
    encoding.marks.channel === '*'
      ? encoding.marks.properties.color.values[0]
      : encoding.marks.series[0].properties.color.values[0];
  if (field && format.colors.map) {
    let colors = [...format.colors.custom];
    if (colors.length === 0) {
      const defaultPalette = palettes.find((p) => p.token === snapshot.colorPaletteToken);
      colors = defaultPalette?.value.split(',') ?? [];
    }
    colors = colors.map((v) => v.trim().toUpperCase());
    // Create the default indexed color map.
    let indexedColorMap: Record<string, number> = {};
    for (const [key, value] of Object.entries(format.colors.map)) {
      indexedColorMap[key] = colors.indexOf(value.trim().toUpperCase());
    }

    // For dates, convert formatted values.
    const compiledField = model.compileEncField(field);
    if (compiledField.isAttrDateType) {
      // DateParts naturally prune the list since there are (mostly)
      // a finite set of possible values.

      // For date parts, convert formatted values to integers...
      if (compiledField.hasDatePart) {
        let datePartMap: Record<string, number> | undefined;
        switch (compiledField.dateFuncUnit) {
          case DateTime.Unit.Quarter:
            datePartMap = QuarterNamesByNumber;
            break;
          case DateTime.Unit.Month:
            datePartMap = MonthNamesByNumber;
            break;
          case DateTime.Unit.Weekday:
            datePartMap = WeekdayNamesByNumber;
            break;
          case DateTime.Unit.Day:
            datePartMap = {};
            for (let i = 1; i <= 31; i++) {
              datePartMap['' + i] = i;
            }
            break;
          case DateTime.Unit.Hour:
            datePartMap = {};
            for (let i = 0; i < 24; i++) {
              datePartMap['' + i] = i;
            }
            break;
          case DateTime.Unit.Minute:
          case DateTime.Unit.Second:
            datePartMap = {};
            for (let i = 0; i < 60; i++) {
              datePartMap['' + i] = i;
            }
            break;
          default:
            break;
        }
        // For anything other than 'year', build a new map based on the possible
        // set of values.
        const newIndexedColorMap: Record<string, number> = {};
        if (datePartMap) {
          for (const [key, value] of Object.entries(datePartMap)) {
            if (Object.prototype.hasOwnProperty.call(indexedColorMap, key)) {
              newIndexedColorMap['' + value] = indexedColorMap[key];
            }
          }
          newIndexedColorMap[MISSING_VALUE] = indexedColorMap[MISSING_VALUE];
          indexedColorMap = newIndexedColorMap;
        } else {
          // This is the callback for validating map keys. It must be a valid year which is:
          //  - an integer between 1500-9999
          //  - null
          // This will only get used for the rare condition that we don't have a current
          // color domain list.
          const isValidYear = (value: string) => {
            if (value == MISSING_VALUE) {
              return true;
            }
            if (!Number.isInteger(parseFloat(value))) {
              return false;
            }
            const result = parseInt(value);
            return result >= 1500 && result <= 9999;
          };
          // Prune the map to remove non-year entries and keep below the threshold size.
          indexedColorMap = pruneColorMapSize(indexedColorMap, currentColorDomain, isValidYear);
        }
      }

      // ...or for date truncs, convert formatted values to milliseconds.
      else if (format.colors.map) {
        // Filter the map entries, because some may not match this field or date unit.
        const dateUnit = compiledField.dateFuncUnit?.toLocaleLowerCase() as keyof typeof DateTimePatterns;
        const pattern: RegExp | undefined = dateUnit ? DateTimePatterns[dateUnit] : DateTimePatterns['second'];
        let filteredEntries = Object.entries(format.colors.map).filter(
          ([key]) => key === MISSING_VALUE || pattern?.test(key)
        );
        // If there are too many entries, trim the list down to the limit.
        // We need to do this before converting the formatted values into
        // raw values since the color domain contains formatted values.
        if (filteredEntries.length > ChartTypes.COLOR_DOMAIN_DATA_LIMIT) {
          if (currentColorDomain.length === 0) {
            // We know that the entries have the proper format but there are too many.
            // Just take a slice of the list.
            filteredEntries = filteredEntries.slice(0, ChartTypes.COLOR_DOMAIN_DATA_LIMIT);
          } else {
            // Pull the values out of the map that match the current color domain
            // up to the data limit.
            const validEntries: Array<[string, string]> = [];
            let count = 0;
            currentColorDomain.some((value) => {
              const key = value == null ? MISSING_VALUE : value;
              const colorEntry = filteredEntries.find((entry) => entry[0] === key);
              if (colorEntry != null) {
                validEntries.push(colorEntry);
                count++;
              }
              return count >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT;
            });
            filteredEntries = validEntries;
          }
        }
        // Now that we have a set of valid entries that is under the data limit
        // we can convert them to timestamps.
        //
        // Ensure that the filtered entries can be parsed as dates.
        switch (compiledField.dateFuncUnit) {
          case DateTime.Unit.Year:
            filteredEntries.forEach((entry) => {
              if (entry[0] !== MISSING_VALUE) {
                entry[0] = 'Jan 01 ' + entry[0];
              }
            });
            break;
          case DateTime.Unit.Quarter:
            filteredEntries.forEach((entry) => {
              entry[0] = entry[0]
                .replace('Q1', 'Jan 01')
                .replace('Q2', 'Apr 01')
                .replace('Q3', 'Jul 01')
                .replace('Q4', 'Oct 01');
            });
            break;
          case DateTime.Unit.Hour:
            filteredEntries.forEach((entry) => {
              entry[0] = entry[0].replace('am', ':00 am').replace('pm', ':00 pm');
            });
            break;
          case DateTime.Unit.Minute:
          case DateTime.Unit.Second:
          case undefined: // Exact date and time
            filteredEntries.forEach((entry) => {
              entry[0] = entry[0].replace('am', ' am').replace('pm', ' pm');
            });
            break;
          default:
            break;
        }

        // Parse the dates to create a new indexed color map with keys in milliseconds.
        if (filteredEntries) {
          let newIndexedColorMap: Record<string, number> = {};
          for (const [key, value] of filteredEntries) {
            const normalizedValue = value.trim().toUpperCase();
            if (key === MISSING_VALUE) {
              newIndexedColorMap[key] = colors.indexOf(normalizedValue);
            } else {
              const ms = Date.parse(key);
              const offset = new Date(ms).getTimezoneOffset();
              if (!Number.isNaN(offset)) {
                const newKey = '' + (ms - 1000 * 60 * offset);
                newIndexedColorMap[newKey] = colors.indexOf(normalizedValue);
              }
            }
          }
          indexedColorMap = newIndexedColorMap;
        }
      }
    } else {
      // For non-dates we don't know if the values in the map are for the current
      // field or not.
      // If the map is too big, we will prune the size down based on the current color domain.
      if (Object.keys(indexedColorMap).length > ChartTypes.COLOR_DOMAIN_DATA_LIMIT) {
        indexedColorMap = pruneColorMapSize(indexedColorMap, currentColorDomain);
      }
    }

    // Create the new FieldColorMap from the indexed color map.
    fieldColors = createFieldColors(snapshot, {
      colorPaletteToken: snapshot.colorPaletteToken,
      colorMap: indexedColorMap,
      palette: colors,
      varType: field?.variable.type || VariableType.DISCRETE,
      formula: field?.formula || '',
    });
  }

  return fieldColors;
}

function pruneColorMapSize(
  indexedColorMap: Record<string, number>,
  currentColorDomain: ReadonlyArray<string | null | undefined>,
  isValidValueFunc?: (value: string) => boolean
) {
  // If we don't have a current color domain, then try to
  // get valid values to put in the new map up to the size limit.
  if (currentColorDomain.length === 0) {
    // No current color domain values and no way to validate
    // values in the map, so just clear the map.
    if (isValidValueFunc == null) {
      return {};
    }
    // Build a new map using the validated values from
    // the old map.
    const newIndexedColorMap: Record<string, number> = {};
    let count = 0;
    Object.keys(indexedColorMap).some((value) => {
      if (isValidValueFunc(value)) {
        newIndexedColorMap[value] = indexedColorMap[value];
        count++;
      }
      return count >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT;
    });
    return newIndexedColorMap;
  }
  // Use the current color domain to filter the map to just the
  // current color domain (up to the color domain limit)
  const newIndexedColorMap: Record<string, number> = {};
  let count = 0;
  currentColorDomain.some((value) => {
    const key = value == null ? MISSING_VALUE : value;
    if (Object.prototype.hasOwnProperty.call(indexedColorMap, key)) {
      newIndexedColorMap[key] = indexedColorMap[key];
      count++;
    }
    return count >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT;
  });
  return newIndexedColorMap;
}

/**
 * Use when converting from Muze to Highcharts to convert the
 * fieldColors map into a single format color map. The fieldColors
 * map is keyed on unformatted values. The format color map
 * requires the formatted value as keys.
 */
export function convertFieldColorsToFormats(
  model: SchemaModel,
  encoding: Readonly<Encoding>,
  fieldColors: Readonly<ChartTypes.FieldColorMap>,
  format: ChartTypes.ChartFormat,
  palette: Readonly<string[]>
) {
  // For any colors in the encoding, put the values->colors in the map
  const colorEncFields: Array<EncField | undefined> = [];
  if (encoding.marks.channel === '*') {
    colorEncFields.push(encoding.marks.properties.color.values[0]);
  } else {
    encoding.marks.series.forEach((series) => {
      colorEncFields.push(series.properties.color.values[0]);
    });
  }
  const formats: Record<string, string> = {};
  const customColors = new Set<string>(palette);
  let colorPaletteToken: string | undefined;
  colorEncFields.forEach((colorField) => {
    if (colorField != null) {
      const compiled = model.compileEncField(colorField);
      const fieldColor = resolveFieldColor(compiled, fieldColors);
      if (fieldColor !== undefined) {
        const seriesNameFormatter = makeSeriesNameFormatter(compiled, format);
        const valueToColorIndexMap = fieldColor.map;
        Object.keys(valueToColorIndexMap).some((rawValue) => {
          // Map keys are always strings even though the raw value may be numeric
          // Convert the strings to numbers as necessary.
          const value = (() => {
            if (compiled.isAttrDateType || compiled.isAttrNumberType) {
              return rawValue !== 'null' ? parseFloat(rawValue) : null;
            }
            return rawValue;
          })();
          const formattedValue = seriesNameFormatter(value) ?? 'null';
          const colorPalette = fieldColor.palette ?? palette;
          const index = valueToColorIndexMap[rawValue];
          const color = (
            index === -1 ? (fieldColor.unassignedColor ?? DEFAULT_UNASSIGNED_COLOR) : (colorPalette[index] ?? '')
          )
            .trim()
            .toUpperCase();
          formats[formattedValue] = color;
          customColors.add(color);
          colorPaletteToken = fieldColor.colorPaletteToken;
          return Object.keys(formats).length >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT;
        });
      }
    }
  });

  const custom = Array.from(customColors.values());
  return { colors: { custom, map: formats }, colorPaletteToken };
}

export function useCompactAxis(oldChartType: ChartTypes.ChartType) {
  switch (oldChartType) {
    case 'area':
    case 'bar':
    case 'hBar':
    case 'hStackedBar':
    case 'hStackedBar100':
    case 'line':
    case 'linePlusBar':
    case 'scatter':
    case 'stackedBar':
    case 'stackedBar100':
    case 'totalArea':
      return true;
    default:
      return false;
  }
}

export function isMuzeFormatChange(oldFormat: ChartTypes.ChartFormat, newFormat: ChartTypes.ChartFormat) {
  // Currently the only property that Muze uses from the ChartFormat is the xAxis compact property
  return oldFormat.xAxis.compact !== newFormat.xAxis.compact;
}

export function generateDefaultSequentialColor(formula: string, palette: OrgTypes.ColorPalette) {
  const paletteColors = palette.value.split(',').map((color) => color.trim().toUpperCase());
  const fieldColor = {
    colorPaletteToken: palette.token,
    palette: paletteColors,
    map: {},
    gradient: {
      gradientType: ChartTypes.GradientType.SEQUENTIAL,
      colorEntries: {
        [ChartTypes.GradientStep.DATA_MIN]: paletteColors[0],
        [ChartTypes.GradientStep.DATA_MAX]: paletteColors[1],
      },
    },
  };

  return { [formula]: fieldColor };
}
